import React from 'react'
import { Icon, Sheet, SheetContent } from '@shopflo/ui'
import { X } from '@phosphor-icons/react'
import { ShoppingCart } from 'react-feather'
import { useTranslation } from 'react-i18next'

const EXIT_COUPON_SPIN_STYLE = (
  <style>{`@keyframes exitCouponConicSpin { to { transform: rotate(360deg); } }`}</style>
)

export type ExitNudgePreviewOption = 'SURVEY' | 'EXIT_CONFIRMATION' | 'EXIT_COUPON_NUDGE' | 'URGENCY_NUDGE'

interface ExitCouponSheetProps {
  open: boolean
  onOpenChange?: (open: boolean) => void
  container: HTMLElement | null
  nudgeType?: ExitNudgePreviewOption
  primaryColor?: string
  primaryTextColor?: string
  // EXIT_COUPON_NUDGE props
  badgeText?: string
  nudgeMessage?: string
  badgeColor?: string
  badgeTextColor?: string
  nudgeTextColor?: string
  couponValue?: string
  // URGENCY_NUDGE props
  urgencyTitle?: string
  urgencyFomoMessage?: string
  urgencyTextColor?: string
}

const ExitCouponContent: React.FC<Pick<ExitCouponSheetProps, 'badgeText' | 'nudgeMessage' | 'badgeColor' | 'badgeTextColor' | 'nudgeTextColor' | 'primaryColor' | 'primaryTextColor' | 'couponValue' | 'onOpenChange'>> = ({
  badgeText,
  nudgeMessage,
  badgeColor = '#2C874A',
  badgeTextColor = '#FFFFFF',
  nudgeTextColor = '#2C874A',
  primaryColor = '#18181B',
  primaryTextColor = '#FFFFFF',
  couponValue = '₹250',
  onOpenChange
}) => {
  const { t } = useTranslation()
  return (
    <div className='relative flex flex-col items-stretch'>
      {EXIT_COUPON_SPIN_STYLE}
      <div className='absolute top-0 left-5 z-10 -translate-y-1/2'>
        <div
          className='px-2.5 py-0.5 rounded-full text-text-xs-semibold'
          style={{ backgroundColor: badgeColor, color: badgeTextColor }}
        >
          {badgeText || t('exit_nudge.preview_badge_default')}
        </div>
      </div>

      <div className='relative z-0 bg-white flex flex-col rounded-t-2xl overflow-hidden'>
        <div className='relative overflow-hidden'>
          <div
            aria-hidden
            className='pointer-events-none absolute z-0 left-[15%] top-[64%] -translate-x-1/2 -translate-y-1/2'
          >
            <div
              className='w-[600px] h-[600px] will-change-transform animate-[exitCouponConicSpin_24s_linear_infinite]'
              style={{
                background: `conic-gradient(from 195deg at 50% 50%, color-mix(in srgb, ${badgeColor} 30%, transparent) 0deg, color-mix(in srgb, ${badgeColor} 4%, transparent) 47deg, color-mix(in srgb, ${badgeColor} 30%, transparent) 96deg, color-mix(in srgb, ${badgeColor} 4%, transparent) 121deg, color-mix(in srgb, ${badgeColor} 30%, transparent) 142deg, color-mix(in srgb, ${badgeColor} 4%, transparent) 161deg, color-mix(in srgb, ${badgeColor} 30%, transparent) 181deg, color-mix(in srgb, ${badgeColor} 4%, transparent) 198deg, color-mix(in srgb, ${badgeColor} 30%, transparent) 223deg, transparent 245deg, color-mix(in srgb, ${badgeColor} 30%, transparent) 260deg, rgba(255, 255, 255, 0) 282deg, color-mix(in srgb, ${badgeColor} 30%, transparent) 310deg, rgba(255, 255, 255, 0) 334deg, color-mix(in srgb, ${badgeColor} 30%, transparent) 360deg)`,
              }}
            />
          </div>
          <div
            className='relative z-10 flex w-full flex-col items-end px-5 pt-3.5 pb-5'
            style={{
              background: `radial-gradient(circle at 50% 56%, rgba(255,255,255,0) 20%, rgba(255,255,255,1) 100%)`,
            }}
          >
            <div className='flex items-center justify-between w-full mb-1'>
              <div className='flex-1' />
              <Icon IconComponent={X} size='xs' className='text-zinc-500 cursor-pointer' onClick={() => onOpenChange?.(false)} iconProps={{ weight: 'bold' }} />
            </div>

            <div className='flex items-end justify-between w-full px-1.5'>
              <div className='flex flex-col gap-1'>
                <span
                  className='text-text-xs-semibold'
                  style={{ color: nudgeTextColor }}
                >
                  {nudgeMessage || t('exit_nudge.preview_nudge_default')}
                </span>
                <div className='flex items-center gap-1'>
                  <span className='text-[26px] font-bold leading-[33px] tracking-[-0.02em] text-zinc-900'>
                    {couponValue}
                  </span>
                  <span className='text-[21px] font-semibold leading-[28px] tracking-[-0.02em] text-zinc-900/50'>
                    {t('exit_nudge.preview_off')}
                  </span>
                </div>
              </div>

              <div className='flex flex-col items-end gap-1 pb-1'>
                <div className='flex items-center gap-1'>
                  <ShoppingCart size={10} className='text-zinc-700/40' />
                  <span className='text-text-xxs-medium text-zinc-700'>{t('exit_nudge.preview_your_cart')}</span>
                </div>
                <div className='flex items-center gap-1'>
                  <span className='text-text-xs-medium text-zinc-700/60 line-through'>1500</span>
                  <span className='text-text-xs-semibold text-zinc-700'>₹1250</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='flex flex-col gap-2 px-5 pb-5'>
          <button
            type='button'
            className='w-full cursor-pointer py-3 rounded-[10px] text-text-md-semibold touch-manipulation'
            style={{ backgroundColor: primaryColor, color: primaryTextColor }}
          >
            {t('exit_nudge.preview_apply_offer')}
          </button>
          <button
            type='button'
            className='w-full cursor-pointer py-3 rounded-[10px] text-text-md-semibold touch-manipulation border border-zinc-900/20 text-zinc-900/70 active:bg-zinc-500/10'
            onClick={() => onOpenChange?.(false)}
          >
            {t('exit_nudge.preview_leave_checkout')}
          </button>
        </div>
      </div>
    </div>
  )
}

const SurveyContent: React.FC<{ onOpenChange?: (open: boolean) => void; primaryColor?: string; primaryTextColor?: string }> = ({ onOpenChange, primaryColor = '#18181B', primaryTextColor = '#FFFFFF' }) => {
  const { t } = useTranslation()
  const options = [
    t('exit_nudge.preview_survey_option_1'),
    t('exit_nudge.preview_survey_option_2'),
    t('exit_nudge.preview_survey_option_3'),
    t('exit_nudge.preview_survey_option_4'),
    t('exit_nudge.preview_survey_option_5'),
    t('exit_nudge.preview_survey_option_6'),
    t('exit_nudge.preview_survey_option_7'),
    t('exit_nudge.preview_survey_option_8'),
    t('exit_nudge.preview_survey_option_9'),
  ]
  return (
    <div className='bg-white rounded-t-2xl px-5 pt-5 pb-6 flex flex-col gap-4'>
      <div className='flex items-center justify-between'>
        <span className='text-text-md-semibold text-zinc-900'>{t('exit_nudge.preview_survey_why_leaving')}</span>
        <Icon IconComponent={X} size='xs' className='text-zinc-500 cursor-pointer' onClick={() => onOpenChange?.(false)} iconProps={{ weight: 'bold' }} />
      </div>
      <div className='flex flex-col gap-3'>
        {options.map(option => (
          <div key={option} className='flex items-center gap-2'>
            <div className='w-4 h-4 rounded-full flex-shrink-0' style={{ border: '1.5px solid #D1D5DC', background: '#FFFFFF', boxShadow: '0 1.5px 0.5px -0.5px rgba(0,0,0,0.04)' }} />
            <span className='text-text-sm-regular text-zinc-800 opacity-70'>{option}</span>
          </div>
        ))}
      </div>
      <button type='button' className='w-full cursor-pointer py-3 rounded-[10px] text-text-md-semibold touch-manipulation mt-1' style={{ backgroundColor: primaryColor, color: primaryTextColor }}>
        {t('exit_nudge.preview_survey_submit')}
      </button>
      <button
        type='button'
        className='w-full cursor-pointer py-3 rounded-[10px] text-text-md-semibold touch-manipulation border border-zinc-900/20 text-zinc-900/70 active:bg-zinc-500/10'
        onClick={() => onOpenChange?.(false)}
      >
        {t('exit_nudge.preview_survey_skip')}
      </button>
    </div>
  )
}

const ExitConfirmationContent: React.FC<{ onOpenChange?: (open: boolean) => void; primaryColor?: string; primaryTextColor?: string }> = ({ onOpenChange, primaryColor = '#18181B', primaryTextColor = '#FFFFFF' }) => {
  const { t } = useTranslation()
  return (
    <div className='bg-white rounded-t-2xl px-5 pt-5 pb-6 flex flex-col'>
      <div className='flex items-start justify-between mb-1'>
        <span className='text-text-lg-semibold text-zinc-900 pr-4'>{t('exit_nudge.preview_exit_confirmation_header')}</span>
        <Icon IconComponent={X} size='xs' className='text-zinc-500 flex-shrink-0 cursor-pointer' onClick={() => onOpenChange?.(false)} iconProps={{ weight: 'bold' }} />
      </div>
      <p className='text-text-sm-medium mb-5 text-zinc-500'>
        {t('exit_nudge.preview_exit_confirmation_body')}
      </p>
      <div className='flex flex-col gap-2'>
        <button type='button' className='w-full cursor-pointer py-3 rounded-[10px] text-text-md-semibold touch-manipulation' style={{ backgroundColor: primaryColor, color: primaryTextColor }}>
          {t('exit_nudge.preview_stay_in_checkout')}
        </button>
        <button
          type='button'
          className='w-full cursor-pointer py-3 rounded-[10px] text-text-md-semibold touch-manipulation border border-zinc-900/20 text-zinc-900/70 active:bg-zinc-500/10'
          onClick={() => onOpenChange?.(false)}
        >
          {t('exit_nudge.preview_leave_checkout')}
        </button>
      </div>
    </div>
  )
}

const UrgencyContent: React.FC<Pick<ExitCouponSheetProps, 'urgencyTitle' | 'urgencyFomoMessage' | 'urgencyTextColor' | 'primaryColor' | 'primaryTextColor' | 'onOpenChange'>> = ({
  urgencyTitle,
  urgencyFomoMessage,
  urgencyTextColor = '#27272A',
  primaryColor = '#18181B',
  primaryTextColor = '#FFFFFF',
  onOpenChange
}) => {
  const { t } = useTranslation()
  return (
    <div className='bg-white rounded-t-2xl px-5 pt-5 pb-6 flex flex-col'>
      <div className='flex items-start justify-between mb-1'>
        <span className='text-text-lg-semibold text-zinc-900 pr-4'>
          {urgencyTitle || t('exit_nudge.preview_urgency_title_default')}
        </span>
        <Icon IconComponent={X} size='xs' className='text-zinc-500 flex-shrink-0 cursor-pointer ml-3' onClick={() => onOpenChange?.(false)} iconProps={{ weight: 'bold' }} />
      </div>
      <p className='text-text-sm-medium mb-5 line-clamp-2' style={{ color: urgencyTextColor }}>
        {urgencyFomoMessage || t('exit_nudge.preview_urgency_fomo_default')}
      </p>
      <div className='flex flex-col gap-3'>
        <button type='button' className='w-full cursor-pointer py-3 rounded-[10px] text-text-md-semibold touch-manipulation' style={{ backgroundColor: primaryColor, color: primaryTextColor }}>
          {t('exit_nudge.preview_stay_in_checkout')}
        </button>
        <button
          type='button'
          className='w-full cursor-pointer py-3 rounded-[10px] text-text-md-semibold touch-manipulation border border-zinc-900/20 text-zinc-900/70 active:bg-zinc-500/10'
          onClick={() => onOpenChange?.(false)}
        >
          {t('exit_nudge.preview_leave_anyway')}
        </button>
      </div>
    </div>
  )
}

const ExitCouponSheet: React.FC<ExitCouponSheetProps> = ({
  open,
  onOpenChange,
  container,
  nudgeType = 'EXIT_COUPON_NUDGE',
  primaryColor,
  primaryTextColor,
  badgeText,
  nudgeMessage,
  badgeColor,
  badgeTextColor,
  nudgeTextColor,
  couponValue,
  urgencyTitle,
  urgencyFomoMessage,
  urgencyTextColor
}) => {
  const renderContent = () => {
    switch (nudgeType) {
      case 'SURVEY':
        return <SurveyContent onOpenChange={onOpenChange} primaryColor={primaryColor} primaryTextColor={primaryTextColor} />
      case 'EXIT_CONFIRMATION':
        return <ExitConfirmationContent onOpenChange={onOpenChange} primaryColor={primaryColor} primaryTextColor={primaryTextColor} />
      case 'URGENCY_NUDGE':
        return (
          <UrgencyContent
            urgencyTitle={urgencyTitle}
            urgencyFomoMessage={urgencyFomoMessage}
            urgencyTextColor={urgencyTextColor}
            primaryColor={primaryColor}
            primaryTextColor={primaryTextColor}
            onOpenChange={onOpenChange}
          />
        )
      case 'EXIT_COUPON_NUDGE':
      default:
        return (
          <ExitCouponContent
            badgeText={badgeText}
            nudgeMessage={nudgeMessage}
            badgeColor={badgeColor}
            badgeTextColor={badgeTextColor}
            nudgeTextColor={nudgeTextColor}
            primaryColor={primaryColor}
            primaryTextColor={primaryTextColor}
            couponValue={couponValue}
            onOpenChange={onOpenChange}
          />
        )
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side='bottom'
        container={container}
        withinContainer
        overlayClassName='!ui-bg-black/40'
        className='!ui-p-0 !ui-border-0 !ui-rounded-t-none !ui-bg-transparent !ui-shadow-none !overflow-visible'
      >
        {renderContent()}
      </SheetContent>
    </Sheet>
  )
}

export default ExitCouponSheet